import { useMutation } from '@apollo/client';
import { ACCOUNT_GET_BUYER_APPLICATIONS } from '@application/api/account/applications/useGetBuyerApplications';
import { useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  MtApplicationsBuyerCheckoutApplicationMutation,
  MtApplicationsBuyerCheckoutApplicationMutationVariables,
} from '@gql/graphql';
import { useCallback } from 'react';

export const MT_APPLICATIONS_BUYER_CHECKOUT_APPLICATION = graphql(`
  mutation MtApplicationsBuyerCheckoutApplication($input: CheckoutApplicationInput!) {
    checkoutApplication(input: $input)
  }
`);

export const useCheckoutApplication = () => {
  const { translatedRoute, routeLocationDescriptor, language, translate } = useRouter();
  const { showToast } = useShowToast();

  const [checkout, { loading }] = useMutation<
    MtApplicationsBuyerCheckoutApplicationMutation,
    MtApplicationsBuyerCheckoutApplicationMutationVariables
  >(MT_APPLICATIONS_BUYER_CHECKOUT_APPLICATION, {
    awaitRefetchQueries: true,
    refetchQueries: [ACCOUNT_GET_BUYER_APPLICATIONS],
  });

  const checkoutApplication = useCallback(
    async (applicationId: number) => {
      const redirectTo = routeLocationDescriptor('mtApplicationCheckoutSuccess', {
        search: { applicationId },
      }) as any;

      const response = await checkout({
        variables: {
          input: {
            applicationId,
            language,
            cancelUrl: `${window.location.origin}${translatedRoute('mtApplicationCheckoutFailed')}`,
            successUrl: `${window.location.origin}${redirectTo.pathname + redirectTo.search}`,
          },
        },
      });
      const url = response.data?.checkoutApplication;

      if (!url) {
        showToast(
          translate('common.toasts.title.error'),
          { type: 'error' },
          { contactError: 'checkoutApplication : no url found' },
        );
        return;
      }

      return url;
    },
    [checkout, language, routeLocationDescriptor, showToast, translate, translatedRoute],
  );

  const checkoutApplicationAndRedirect = useCallback(
    async (applicationId: number) => {
      const url = await checkoutApplication(applicationId);
      if (url) {
        window.location.href = url;
      }
    },
    [checkoutApplication],
  );

  return {
    checkoutApplication,
    checkoutApplicationLoading: loading,
    checkoutApplicationAndRedirect,
  };
};
